(ns ui.components.sortable-list.table
  (:require
   [react :as react]
   [reagent.core :as r]
   ["@atlaskit/pragmatic-drag-and-drop/combine" :refer [combine]]
   ["@atlaskit/pragmatic-drag-and-drop/element/adapter" :refer [draggable dropTargetForElements monitorForElements]]
   ["@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge" :refer [attachClosestEdge extractClosestEdge]]
   ["@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index" :refer [getReorderDestinationIndex]]
   ["@atlaskit/pragmatic-drag-and-drop-auto-scroll/element" :refer [autoScrollForElements]]
   [oops.core :as oops]
   [ui.components.sortable-list.view :as sortable]
   [ui.util :as uu]))

(defn list-item-inner-table-row
  [_]
  (let [element-ref* (atom nil)
        drag-handle-ref* (atom nil)
        draggable-state* (r/atom sortable/idle-state)
        closest-edge* (r/atom nil)]
    (fn [{:keys [id index item register-item instance-id
                 drag-handle? orientation gap
                 render-item render-drag-handle
                 tr-class style drop-indicator-type]
          :or {drag-handle? true
               orientation :vertical
               gap "0px"
               style {}}}]

      (react/useEffect
       (fn []
         (let [element @element-ref*
               drag-handle (if drag-handle?
                             @drag-handle-ref*
                             element)
               data (clj->js
                     (sortable/get-item-data
                      {:item item
                       :index index
                       :instance-id instance-id}))

               allowed-edges (case orientation
                               :vertical ["top" "bottom"]
                               :horizontal ["left" "right"])

               on-change (fn [js-args]
                           (let [source (oops/oget js-args "source")
                                 self (oops/oget js-args "self")]
                             (if (= (oops/oget source "element") drag-handle)
                               (reset! closest-edge* nil)
                               (let [closest-edge (extractClosestEdge (oops/oget self "data"))
                                     source-data (oops/oget source "data")
                                     source-index (oops/oget source-data "index")

                                     _ (assert (number? source-index))

                                     is-item-before-source? (= index (dec source-index))
                                     is-item-after-source? (= index (inc source-index))

                                     drop-indicator-hidden? (case orientation
                                                              :vertical (or (and is-item-before-source?
                                                                                 (= closest-edge "bottom"))
                                                                            (and is-item-after-source?
                                                                                 (= closest-edge "top")))
                                                              :horizontal (or (and is-item-before-source?
                                                                                   (= closest-edge "right"))
                                                                              (and is-item-after-source?
                                                                                   (= closest-edge "left"))))]
                                 (if drop-indicator-hidden?
                                   (reset! closest-edge* nil)
                                   (reset! closest-edge* closest-edge))))))]

           (assert element)
           (assert drag-handle)

           (combine
            (register-item
             {:item-id id
              :element element})

            (draggable
             (clj->js
              {:element element
               :dragHandle drag-handle
               :getInitialData (fn [] data)
               :onDragStart (fn [_ev]
                              (reset! draggable-state* sortable/dragging-state))
               :onDrop (fn []
                         (reset! draggable-state* sortable/idle-state))}))

            (dropTargetForElements
             (clj->js
              {:element element
               :canDrop (fn [js-args]
                          (let [source (oops/oget js-args "source" "data")]
                            (and (sortable/item-data? (js->clj source
                                                               :keywordize-keys true))
                                 (= (oops/oget source "instance-id") instance-id))))
               :getIsSticky (constantly true)
               :getData (fn [js-args]
                          (let [data (attachClosestEdge
                                      (clj->js data)
                                      (clj->js
                                       {:element element
                                        :input (oops/oget js-args "input")
                                        :allowedEdges allowed-edges}))]
                            data))
               :onDragEnter on-change
               :onDrag on-change
               :onDragLeave #(reset! closest-edge* nil)
               :onDrop #(reset! closest-edge* nil)})))))

       (clj->js [item index register-item instance-id]))

      (let [state @draggable-state*
            state-type (:type state)
            dragging? (= state-type :dragging)
            _preview? (= state-type :preview)
            drag-handle-component (when drag-handle?
                                    (if render-drag-handle
                                      [:td {:ref #(reset! drag-handle-ref* %)}
                                       [render-drag-handle {:dragging? dragging?}]]
                                      [:td
                                       {:ref #(reset! drag-handle-ref* %)
                                        :class "cursor-grab hover:cursor-grab py-1 px-px rounded flex items-center justify-center bg-complement-light hover:bg-complement-regular"}
                                       [sortable/drag-handle-icon
                                        {:size 16}]]))]

        [:tr
         {:ref #(reset! element-ref* %)
          :class (uu/cm "relative"
                        "hover:translate-x-0"
                        tr-class)
          :style (merge {:opacity (if dragging? 0.4 1)}
                        style)}

         [render-item {:item item
                       :index index
                       :dragging? dragging?
                       :drag-handle drag-handle-component}]

         (when @closest-edge*
           [sortable/drop-indicator
            {:orientation orientation
             :edge (keyword @closest-edge*)
             :gap gap
             :appearance :default
             :indicator-type drop-indicator-type}])]))))

(defn list-item-table-row
  [props]
  [:f> list-item-inner-table-row props])

(defn sortable-list-inner-table-row
  [_]
  (let [list-ref* (atom nil)
        registry (sortable/get-item-registry)
        instance-id (sortable/get-instance-id)]

    (fn [{:keys [items get-key render-item render-drag-handle on-reorder
                 orientation drag-handle? gap tr-class
                 auto-scroll? drop-indicator-type]
          :or {get-key :id
               orientation :vertical
               drag-handle? false
               gap 0
               auto-scroll? false}}]
      (let [items (vec items)]

        (react/useEffect
         (fn []
           (let [list-element @list-ref*]
             (assert list-element)
             (combine
              (if auto-scroll?
                (autoScrollForElements
                 (clj->js
                  {:element list-element}))
                identity)

              (monitorForElements
               (clj->js
                {:canMonitor (fn [js-args]
                               (let [source-js (oops/oget js-args "source" "data")
                                     source-clj (js->clj source-js :keywordize-keys true)
                                     valid-item? (sortable/item-data? source-clj)
                                     same-instance? (= (:instance-id source-clj) instance-id)
                                     can-monitor? (and valid-item? same-instance?)]
                                 can-monitor?))
                 :onDrop (fn [js-args]
                           (let [source (oops/oget js-args "source" "data")
                                 location (oops/oget js-args "location")
                                 drop-targets (oops/oget location "current" "dropTargets")]
                             (when (-> drop-targets
                                       (oops/oget "length")
                                       (> 0))
                               (let [target (aget drop-targets 0)
                                     source-data (js->clj source :keywordize-keys true)
                                     target-data (js->clj (oops/oget target "data") :keywordize-keys true)]

                                 (when (and (sortable/item-data? source-data)
                                            (sortable/item-data? target-data))

                                   (let [source-index (:index source-data)
                                         target-index (:index target-data)

                                         closest-edge-of-target (extractClosestEdge (oops/oget target "data"))

                                         final-index (getReorderDestinationIndex
                                                      (clj->js
                                                       {:axis (name orientation)
                                                        :startIndex source-index
                                                        :indexOfTarget target-index
                                                        :closestEdgeOfTarget closest-edge-of-target}))

                                         item (get items source-index)
                                         ;; left as reference
                                         #_#_item-id (get-key item)]

                                     (when (and (fn? on-reorder)
                                                (not= source-index final-index))
                                       (on-reorder {:item item
                                                    :source-index source-index
                                                    :destination-index final-index})
                                       ;; doesn't work well with table structure.
                                       ;; investigage later
                                       #_(uu/timeout
                                          4
                                          (fn []
                                            (when-let [element ((:get-element registry) item-id)]
                                              (sortable/trigger-post-move-flash element)))))))))))})))))

         (clj->js [instance-id orientation on-reorder]))

        [:tbody
         {:ref #(reset! list-ref* %)
          :style {:gap (str (or gap 0) "px")}}
         (doall
          (->> items
               (map-indexed
                (fn [index item]
                  (let [item-id (get-key item)]
                    ^{:key (str item-id)}
                    [list-item-table-row
                     {:id item-id
                      :item item
                      :index index
                      :instance-id instance-id
                      :register-item (:register registry)
                      :orientation orientation
                      :drag-handle? drag-handle?
                      :gap gap
                      :render-item render-item
                      :render-drag-handle render-drag-handle
                      :tr-class tr-class
                      :drop-indicator-type drop-indicator-type}])))))]))))

(defn sortable-table-list
  [props]
  [:f> sortable-list-inner-table-row props])

(defn view
  [props]
  [sortable-table-list props])
